import { STATUS_PALETTE } from '@components/StatusChip';
import { SYNC_BADGE, SYNC_TYPE, SYNC_VIEW, SyncDiff, ListingDirectoryPlatformId } from '@constants/profiles';

const isScannerProfile = profile => profile?.isFromScanner === true;

const isNotPublishedScannerDiff = profile =>
    isScannerProfile(profile) &&
    Array.isArray(profile?.syncDiff) &&
    profile.syncDiff.length === 1 &&
    profile.syncDiff[0] === SyncDiff.NotPublished;

// Maps each sync badge to a tone in the shared STATUS_PALETTE so chip text + dot
// stay aligned with every other status chip in the app.
const BADGE_TONE = {
    [SYNC_BADGE.DISABLED]: 'neutral',
    [SYNC_BADGE.SYNCED]: 'success',
    [SYNC_BADGE.CONFLICT]: 'warning',
    [SYNC_BADGE.NOT_FOUND]: 'danger',
    [SYNC_BADGE.NOT_VERIFIED]: 'danger',
    [SYNC_BADGE.DISCONNECTED]: 'neutral',
    [SYNC_BADGE.IN_PROGRESS]: 'info',
};

export const STATUS_ACCENT = Object.fromEntries(
    Object.entries(BADGE_TONE).map(([badge, tone]) => [badge, STATUS_PALETTE[tone].fg]),
);

// Donut slices. Saturated hexes shared with Reviews / NPS charts — distinct from
// the chip palette by design (chart fills need higher saturation than chip text).
export const STATUS_FILL = {
    [SYNC_BADGE.DISABLED]: '#9E9E9E',
    [SYNC_BADGE.SYNCED]: '#4CC474',
    [SYNC_BADGE.CONFLICT]: '#FFA949',
    [SYNC_BADGE.NOT_FOUND]: '#FF5853',
    [SYNC_BADGE.NOT_VERIFIED]: '#FF5853',
    [SYNC_BADGE.IN_PROGRESS]: '#1976D2',
};

// MIRROR: SyncableProfilesQueryFactory.IsConflictExpression / IsNotPublishedExpression.
export const getProfileSyncBadge = profile => {
    if (!profile) return SYNC_BADGE.DISABLED;
    switch (profile.syncStatus) {
        case SYNC_TYPE.SYNCED:
        case SYNC_TYPE.SYNCED_WITH_CONFLICTS:
            return SYNC_BADGE.SYNCED;
        case SYNC_TYPE.IN_PROGRESS:
            return SYNC_BADGE.IN_PROGRESS;
        case SYNC_TYPE.DISCONNECTED:
            return SYNC_BADGE.DISCONNECTED;
        case SYNC_TYPE.ACTION_REQUIRED:
            if (profile.isVerified === false) return SYNC_BADGE.NOT_VERIFIED;
            if (isNotPublishedScannerDiff(profile)) return SYNC_BADGE.NOT_FOUND;
            return SYNC_BADGE.CONFLICT;
        case SYNC_TYPE['N/A']:
        default:
            return SYNC_BADGE.DISABLED;
    }
};

const BADGE_BG = Object.fromEntries(
    Object.entries(BADGE_TONE).map(([badge, tone]) => [badge, STATUS_PALETTE[tone].bg]),
);

export const getBadgeColors = badge => ({
    bg: BADGE_BG[badge] ?? BADGE_BG[SYNC_BADGE.DISABLED],
    fg: STATUS_ACCENT[badge] ?? STATUS_ACCENT[SYNC_BADGE.DISABLED],
});

const BADGE_LABEL_KEY = {
    [SYNC_BADGE.SYNCED]: 'common.synced',
    [SYNC_BADGE.CONFLICT]: 'common.conflict',
    [SYNC_BADGE.NOT_FOUND]: 'common.notFound',
    [SYNC_BADGE.NOT_VERIFIED]: 'common.notVerified',
    [SYNC_BADGE.IN_PROGRESS]: 'common.inProgress',
    [SYNC_BADGE.DISCONNECTED]: 'common.disconnected',
    [SYNC_BADGE.DISABLED]: 'common.disabled',
};

export const getBadgeLabel = (badge, t) => t(BADGE_LABEL_KEY[badge] ?? 'common.disabled');

export const VIEW_COLORS = {
    [SYNC_VIEW.DISABLED]: STATUS_FILL[SYNC_BADGE.DISABLED],
    [SYNC_VIEW.SYNCED]: STATUS_FILL[SYNC_BADGE.SYNCED],
    [SYNC_VIEW.CONFLICT]: STATUS_FILL[SYNC_BADGE.CONFLICT],
    [SYNC_VIEW.NOT_PUBLISHED]: STATUS_FILL[SYNC_BADGE.NOT_FOUND],
    [SYNC_VIEW.IN_PROGRESS]: STATUS_FILL[SYNC_BADGE.IN_PROGRESS],
};

export const VIEW_LABEL_KEY = {
    [SYNC_VIEW.DISABLED]: 'common.disabled',
    [SYNC_VIEW.SYNCED]: 'common.synced',
    [SYNC_VIEW.CONFLICT]: 'common.conflict',
    [SYNC_VIEW.NOT_PUBLISHED]: 'common.notPublished',
    [SYNC_VIEW.IN_PROGRESS]: 'common.inProgress',
};

export { isScannerProfile, isNotPublishedScannerDiff };

export const findProfileById = (profiles, profileId) => profiles?.find(item => item.profileId === profileId);

export const isListingDirectoryProfile = profile => profile?.platform === ListingDirectoryPlatformId;
